import { Link } from 'react-router-dom';

/**
 * Getting Started Guide for Models
 * Step-by-step walkthrough of the model experience in MediaRite
 */
export default function ModelGuide() {
  return (
    <div className="page guide-page">
      <div className="page-header">
        <h1>Model Getting Started Guide</h1>
        <p className="page-subtitle">
          Welcome to MediaRite! This guide will walk you through everything you need to know.
        </p>
      </div>

      <div className="guide-content">
        <section className="guide-section">
          <h2>1. Your Dashboard</h2>
          <p>
            After logging in, you'll land on your <Link to="/dashboard">Dashboard</Link>.
            This is your home base where you can:
          </p>
          <ul>
            <li>See pending connection requests from studios</li>
            <li>Access your profile</li>
            <li>View documents that need your signature</li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>2. Connection Requests</h2>
          <p>
            Studios need to connect with you before they can create productions involving you.
            When a studio sends you a connection request:
          </p>
          <ol>
            <li>You'll see it on your Dashboard under "Pending Connection Requests"</li>
            <li>Review the studio name and when they sent the request</li>
            <li>Click <strong>Approve</strong> to allow them to work with you</li>
            <li>Click <strong>Reject</strong> if you don't want to work with them</li>
          </ol>
          <div className="guide-note">
            <strong>Note:</strong> You can disconnect from a studio later through your profile
            if you change your mind.
          </div>
        </section>

        <section className="guide-section">
          <h2>3. Your Profile</h2>
          <p>
            Your <Link to="/my-profile">Profile</Link> contains your personal information
            needed for compliance records:
          </p>
          <ul>
            <li><strong>Legal Name</strong> - Your real legal name (required for 2257 records)</li>
            <li><strong>Stage Names</strong> - Any performer names you use</li>
            <li><strong>Date of Birth</strong> - Required for age verification</li>
            <li><strong>ID Documents</strong> - Scans of your government-issued ID</li>
          </ul>
          <div className="guide-warning">
            <strong>Important:</strong> Your ID documents are stored securely and only
            visible to studios you've connected with. They are required by federal law
            (18 U.S.C. § 2257) for adult content production.
          </div>
        </section>

        <section className="guide-section">
          <h2>4. Documents</h2>
          <p>
            The <Link to="/documents">Documents</Link> page shows all documents associated
            with your work:
          </p>
          <ul>
            <li><strong>Model Releases</strong> - Permission to use your likeness</li>
            <li><strong>2257 Compliance Records</strong> - Age verification records</li>
            <li><strong>Rights Agreements</strong> - Usage rights for content</li>
          </ul>

          <h3>Signing Documents</h3>
          <ol>
            <li>Click on a document to view it</li>
            <li>Review all the terms carefully</li>
            <li>If you agree, click the <strong>Sign</strong> button</li>
            <li>Draw your signature or type it</li>
            <li>Confirm your signature</li>
          </ol>
          <div className="guide-note">
            <strong>Tip:</strong> You can view a document's PDF at any time by clicking
            "Download PDF" on the document detail page.
          </div>
        </section>

        <section className="guide-section">
          <h2>5. Common Workflows</h2>

          <h3>Working with a New Studio</h3>
          <ol>
            <li>Wait for the studio to send you a connection request</li>
            <li>Approve the connection request on your Dashboard</li>
            <li>The studio can now add you to productions</li>
            <li>You'll receive documents to sign for each production</li>
          </ol>

          <h3>Completing a Production</h3>
          <ol>
            <li>The studio creates a production and adds you as a performer</li>
            <li>Required documents are automatically generated</li>
            <li>Sign all pending documents in your Documents list</li>
            <li>The production is complete when all documents are signed</li>
          </ol>
        </section>

        <section className="guide-section">
          <h2>Need Help?</h2>
          <p>
            If you have questions or issues, contact the studio you're working with
            or reach out to MediaRite support.
          </p>
        </section>

        <div className="guide-actions">
          <Link to="/dashboard" className="btn btn-primary">
            Go to Dashboard
          </Link>
          <Link to="/my-profile" className="btn btn-secondary">
            View My Profile
          </Link>
        </div>
      </div>
    </div>
  );
}
